import { css, ThemeProvider as EmotionThemeProvider } from '@emotion/react'
import { rgba } from 'emotion-rgba'
import React, { memo } from 'react'

import { MediaContextProvider, mediaStyle } from './media'

export type Theme = typeof theme

export const theme = {
  colors: {
    variants: {
      neutralLight5: '#FFFFFF',
      neutralLight4: '#F9F5F4',
      neutralLight3: '#E7DED9',
      neutralDark3: '#546084',
      neutralDark2: '#535969',
      neutralDark1: '#000000',
      primaryDark: '#0A122A',
      primaryLight: '#B9A277',
      danger: '#F23030',
    },
  },
  fontFamily: {
    heading: "'condor', sans-serif",
    label: "'lindsey-signature', sans-serif",
    paragraph: "'nimbus-sans', sans-serif",
  },
}

export type Styles = typeof styles

const titleStyle = css`
  color: ${theme.colors.variants.primaryDark};
  font-family: ${theme.fontFamily.heading};
`

export const styles = {
  label: {
    large: css`
      color: ${theme.colors.variants.primaryLight};
      font-family: ${theme.fontFamily.label};
      font-size: 4.5rem;
      line-height: 5.375rem;

      @media (max-width: 1199px) {
        font-size: 3rem;
        line-height: 3rem;
      }
    `,
    medium: css`
      color: ${theme.colors.variants.primaryLight};
      font-family: ${theme.fontFamily.label};
      font-size: 3.75rem;
      line-height: 4.5rem;

      @media (max-width: 1199px) {
        font-size: 2.25rem;
        line-height: 3rem;
      }
    `,
    custom: css`
      color: ${theme.colors.variants.primaryDark};
      font-family: ${theme.fontFamily.paragraph};
      font-size: 0.8125rem;
      letter-spacing: 0.2em;
      line-height: 1rem;
      text-transform: uppercase;
    `,
  },
  title: {
    xl: css`
      ${titleStyle}
      font-size: 6rem;
      line-height: 6rem;
      text-transform: uppercase;

      @media (max-width: 1199px) {
        font-size: 3rem;
        line-height: 3rem;
      }
    `,
    large: css`
      ${titleStyle}
      font-size: 3.75rem;
      line-height: 3.75rem;
      text-transform: uppercase;

      @media (max-width: 1199px) {
        font-size: 2.25rem;
        line-height: 2.25rem;
      }
    `,
    medium: css`
      ${titleStyle}
      font-size: 3rem;
      line-height: 3.3125rem;
      text-transform: uppercase;

      @media (max-width: 1199px) {
        font-size: 2.25rem;
        line-height: 2.25rem;
      }
    `,
    small: css`
      ${titleStyle}
      font-size: 2.25rem;
      line-height: 2.75rem;

      @media (max-width: 1199px) {
        font-size: 1.5rem;
        line-height: 1.75rem;
      }
    `,
    xs: css`
      ${titleStyle}
      font-size: 1.5rem;
      line-height: 1.75rem;
    `,
  },
  description: css`
    color: ${theme.colors.variants.neutralDark2};
    font-family: ${theme.fontFamily.paragraph};
    font-size: 1rem;
    font-weight: 300;
    line-height: 1.375rem;
  `,
  img: css`
    width: 100%;
    height: 100%;
    object-fit: cover;
    position: absolute;
    top: 0;
    left: 0;
    will-change: contents;
  `,
}

export type Datepicker = typeof datepicker

export const datepicker = css`
  .react-datepicker {
    min-width: 20rem;
    font-family: ${theme.fontFamily.paragraph};
    background-color: ${theme.colors.variants.neutralLight4};
    box-shadow: ${rgba(theme.colors.variants.primaryDark, 0.1)} 0 0 1.25rem;
    color: ${theme.colors.variants.primaryDark};
  }

  .react-datepicker__portal {
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${rgba(theme.colors.variants.neutralLight5, 0.8)};
    width: 100%;
    height: 100%;
    position: fixed;
    top: 0;
    left: 0;
    z-index: 999;
  }

  .react-datepicker__current-month {
    font-family: ${theme.fontFamily.paragraph};
    color: ${theme.colors.variants.primaryDark};
  }

  .react-datepicker__day-name {
    font-family: ${theme.fontFamily.paragraph};
  }

  .react-datepicker__day {
    border: 0.0625rem solid ${rgba(theme.colors.variants.primaryDark, 0.15)};
  }

  .react-datepicker__day--in-selecting-range,
  .react-datepicker__day--selected {
    border-color: ${theme.colors.variants.primaryLight};
    background-color: ${theme.colors.variants.primaryLight} !important;
    color: ${theme.colors.variants.neutralLight5} !important;
  }

  .react-datepicker__day:hover {
    background-color: ${theme.colors.variants.primaryDark};
    color: ${theme.colors.variants.neutralLight5};
  }

  .react-datepicker__day:focus {
    background-color: ${theme.colors.variants.primaryDark} !important;
    color: ${theme.colors.variants.neutralLight5} !important;
  }

  .react-datepicker__day--disabled {
    opacity: 0.3;
  }

  .react-datepicker-wrapper,
  .react-datepicker__input-container {
    input {
      background-color: transparent;
      background-image: url('/svg/calendar.svg');
      background-repeat: no-repeat;
      background-position: right center;
      font-family: ${theme.fontFamily.paragraph};
      font-size: 1rem;
      font-weight: 300;
      color: ${theme.colors.variants.primaryDark};
      line-height: 1.375rem;
      &.error {
        border-color: ${theme.colors.variants.danger};
      }

      &::-webkit-input-placeholder {
        color: ${theme.colors.variants.primaryDark};
        opacity: 0.3;
      }
      &::-moz-placeholder {
        color: ${theme.colors.variants.primaryDark};
        opacity: 0.3;
      }
      &::-ms-input-placeholder {
        color: ${theme.colors.variants.primaryDark};
        opacity: 0.3;
      }
      &::placeholder {
        color: ${theme.colors.variants.primaryDark};
        opacity: 0.3;
      }
    }
  }
`

export type Numeric = typeof numeric

export const numeric = css`
  .react-numeric-input {
    display: flex;
    align-items: center;
    position: relative;
    input {
      width: 2.25rem;
      caret-color: transparent;
      color: ${theme.colors.variants.primaryDark};
      font-family: ${theme.fontFamily.paragraph};
      font-size: 1rem;
      font-weight: 700;
      order: 2;
      pointer-events: none;
      text-align: center;
    }
    b {
      width: 1.8125rem;
      height: 1.8125rem;
      background: ${theme.colors.variants.primaryDark};
      border-radius: 50%;
      cursor: pointer;
      position: relative;
      text-align: center;
      transition: 0.1s ease-out;
      &:hover {
        background: ${theme.colors.variants.neutralDark3};
      }
      &:before,
      &:after {
        content: '';
        background: ${theme.colors.variants.neutralLight5};
        position: absolute;
        transition: 0.1s ease-out;
      }
      &:nth-of-type(1) {
        order: 2;
        &:before {
          width: 0.75rem;
          height: 0.0625rem;
          top: 0.875rem;
          left: 0.5rem;
        }
        &:after {
          width: 0.0625rem;
          height: 0.75rem;
          top: 0.5rem;
          left: 0.875rem;
        }
      }
      &:last-of-type {
        &:before {
          width: 0.75rem;
          height: 0.0625rem;
          top: 0.875rem;
          left: 0.5rem;
        }
      }
    }
  }
`

export const ThemeProvider = memo(function ThemeProvider({ children }: any) {
  return (
    <EmotionThemeProvider theme={theme}>
      <style type="text/css">{mediaStyle}</style>
      <MediaContextProvider disableDynamicMediaQueries>
        {children}
      </MediaContextProvider>
    </EmotionThemeProvider>
  )
})
